import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Badge, Card } from '@/components/ui';
import { RefreshCw, Send, ShieldOff } from 'lucide-react';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ?? 'http://127.0.0.1:54321';
const EDGE_FN_URL = `${supabaseUrl}/functions/v1/admin-send-digest-now`;

interface Recipient {
  userId: string;
  // Nullable: a recipient can lack a display name (never set during
  // provisioning) or a verified phone (the `no_phone` eligibility rows the
  // GET list still includes so an operator can see WHY someone can't be
  // sent to, not just that they can't).
  displayName: string | null;
  phoneLast4: string | null;
  email: string | null;
  householdId: string;
  householdName: string | null;
  timezone: string;
  withinWindow: boolean;
  eligible: boolean;
  reason: string | null;
}

interface RecipientsResponse {
  smsKilled: boolean;
  recipients: Recipient[];
}

type SendResult =
  | { outcome: 'enqueued'; tier: string; itemCount: number }
  | { outcome: 'skipped'; reason: string };

export interface AdminSmsProps {
  password: string;
}

/**
 * "Send now" panel for the daily SMS digest. The scheduled digest only fires
 * once per recipient per day inside their evening window, so this is the
 * only way to see a real digest render without waiting a day per copy
 * tweak. It skips the scheduling guards only — consent, verification, and
 * opt-out are still enforced by the edge function.
 *
 * Every outcome shown here comes straight from the response, never an
 * optimistic guess: the failure modes (outside window, cap reached, consent
 * withdrawn) are exactly what the operator needs to see.
 */
export function AdminSms({ password }: AdminSmsProps) {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [smsKilled, setSmsKilled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<Record<string, SendResult>>({});
  // Synchronous lock, separate from `busyIds` state. State updates are
  // batched/async, so a genuine double-click in the same tick could read a
  // stale `busyIds` before the first click's re-render lands. The ref closes
  // that race.
  const busyRef = useRef<Set<string>>(new Set());

  const fetchRecipients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'GET',
        headers: { 'X-Admin-Password': password },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Unauthorized' }));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as RecipientsResponse;
      setSmsKilled(data.smsKilled);
      setRecipients(data.recipients);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load recipients',
      );
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => {
    void fetchRecipients();
  }, [fetchRecipients]);

  const handleSend = useCallback(
    async (userId: string) => {
      if (busyRef.current.has(userId)) return; // double-click guard
      busyRef.current.add(userId);
      setBusyIds((prev) => new Set(prev).add(userId));
      // Clear any stale result from a previous click so this send's outcome
      // reads unambiguously once it lands.
      setResults((prev) => {
        if (!(userId in prev)) return prev;
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      try {
        const res = await fetch(EDGE_FN_URL, {
          method: 'POST',
          headers: {
            'X-Admin-Password': password,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId }),
        });
        const body = await res.json().catch(() => ({}));
        if (res.ok) {
          setResults((prev) => ({ ...prev, [userId]: body as SendResult }));
        } else {
          // Covers the 404 "not a digest recipient" branch, which has no
          // `reason` field — fall back to the top-level `error` string so we
          // never render `undefined`.
          setResults((prev) => ({
            ...prev,
            [userId]: {
              outcome: 'skipped',
              reason: body.reason ?? body.error ?? `HTTP ${res.status}`,
            },
          }));
        }
      } catch (err) {
        setResults((prev) => ({
          ...prev,
          [userId]: {
            outcome: 'skipped',
            reason: err instanceof Error ? err.message : 'Send failed',
          },
        }));
      } finally {
        busyRef.current.delete(userId);
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      }
    },
    [password],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-foreground">
            Send digest now
          </h2>
          <Badge
            variant="info"
            size="sm"
          >
            {recipients.length} recipient{recipients.length === 1 ? '' : 's'}
          </Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void fetchRecipients()}
          loading={loading}
          leftIcon={<RefreshCw size={14} />}
        >
          Refresh
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Sends one recipient&apos;s real digest immediately, skipping only the
        scheduled-time and once-per-day guards. Consent, verification, and
        opt-out are still enforced.
      </p>

      {error ? (
        <div
          role="alert"
          className="px-3 py-2 bg-danger-light border border-danger rounded-md text-danger text-xs"
        >
          {error}
        </div>
      ) : null}

      {smsKilled ? (
        <div
          role="alert"
          className="flex items-center gap-2 px-3 py-2 bg-danger-light border border-danger rounded-md text-danger text-xs"
        >
          <ShieldOff size={14} />
          The SMS kill-switch is on — all sends are disabled until it&apos;s
          turned back off.
        </div>
      ) : null}

      {loading && recipients.length === 0 ? (
        <p className="text-xs text-muted-foreground">Loading recipients...</p>
      ) : recipients.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No digest recipients found.
        </p>
      ) : (
        <Card
          variant="default"
          padding="none"
        >
          <ul className="flex flex-col divide-y divide-border">
            {recipients.map((r) => {
              const busy = busyIds.has(r.userId);
              const result = results[r.userId];
              // The eligibility reason (no_phone, opted_out, ...) is more
              // actionable than a generic window message, so it wins when a
              // recipient fails both checks at once.
              const disabledReason = !r.eligible
                ? (r.reason ?? 'Not eligible')
                : !r.withinWindow
                  ? 'Outside sending window'
                  : null;

              return (
                <li
                  key={r.userId}
                  className="flex flex-col gap-2 p-4 md:flex-row md:items-center md:justify-between"
                >
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">
                        {r.displayName ?? 'Unnamed recipient'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {r.phoneLast4
                          ? `•••• ${r.phoneLast4}`
                          : 'no phone on file'}
                      </span>
                    </div>
                    {r.email ? (
                      <span className="text-xs text-muted-foreground truncate">
                        {r.email}
                      </span>
                    ) : null}
                    <span className="text-xs text-muted-foreground">
                      {r.householdName ?? 'Unknown household'} · {r.timezone}
                    </span>
                    {disabledReason ? (
                      <span className="text-xs text-warning">
                        {disabledReason}
                      </span>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {result ? (
                      result.outcome === 'enqueued' ? (
                        <span
                          role="status"
                          className="text-xs text-success"
                        >
                          Enqueued · {result.tier} · {result.itemCount} item
                          {result.itemCount === 1 ? '' : 's'}
                        </span>
                      ) : (
                        <span
                          role="status"
                          className="text-xs text-warning"
                        >
                          Skipped: {result.reason}
                        </span>
                      )
                    ) : null}
                    {!smsKilled ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!r.withinWindow || !r.eligible}
                        loading={busy}
                        leftIcon={<Send size={12} />}
                        onClick={() => void handleSend(r.userId)}
                        aria-label={`Send now for ${r.displayName ?? 'this recipient'}`}
                      >
                        Send now
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
